import { Injectable } from "@angular/core";
import { RockModelAssessment, RockModelAssessmentType, RockModelPerson, RockModelPersonAlias, RockModelPhoneNumber } from "../rock/Assessments/Assessments.models";
import { AssessmentsService } from "../rock/Assessments/Assessments.service";
import { PeopleService } from "../rock/People/People.service";
import { AttributeValuesService } from "../rock/AttributeValues/AttributeValues.service";
import { RockModelAttributeValue } from "../rock/AttributeValues/AttributeValues.models";

import assessmentTypes from './assets/assessmentTypes.json';
import spiritualGiftsAttributes from './assets/spiritualGiftsAttributes.json';
import scoreToPercentage from './assets/zScoreToPercentage.json';

@Injectable({
		providedIn: 'root'
})
export class SpiritualGiftsService {

		public questions: any = null;
		public categories: GiftCategory[] = null;
	 
		private readonly minSupportiveGiftScore: number = 0.0;
		private questionData: Map<string, string> = new Map();
		private zScoreToPercentage: Map<number, number> = new Map();

		// The constructData. The string value in this map is the GUID for the Spiritual Gift Type
		private constructData: Map<string, SpiritualGift> = new Map();
		
		constructor(
				private assessmentService: AssessmentsService,
				private peopleService: PeopleService,
				private attributeValuesService: AttributeValuesService) {
		}

		changeTest(questions, categories) {
			this.questions = questions;
			this.categories = categories;

			this.constructQuestionData();
		}

		resetTest() {
			this.questionData = new Map();
			this.constructData = new Map();
			this.zScoreToPercentage = new Map();
		}

		constructQuestionData() {
			this.resetTest();

			Object.values(this.questions).map(question => {
				Object.keys(question).map(key => {
					this.questionData.set(key, question[key]);
				});
			});

			Object.values(this.categories).map((category: GiftCategory) => {
				if (category.Constructs.length > 0) {
					this.constructData.set(category.Guid, new SpiritualGift(category.mean, category.standardDeviation, category.Constructs));
				}
			});

			Object.values(scoreToPercentage).map(option => {
				this.zScoreToPercentage.set(option.key, option.value);
			});
		}
		
		GetQuestions(randomizeQuestions: boolean): Map<string, string> {
			if (!randomizeQuestions) {
				return new Map(Array.from(this.questionData));
			}

			const shuffledQuestions = this.shuffleMap(this.questionData);
			const questions: Map<string, string> = new Map(shuffledQuestions);
			return questions;
		}

		shuffleMap(map: Map<string, string>): [string, string][] {
				const entries = Array.from(map.entries());
				const newArray = this.shuffleArray(entries);
				return newArray;
		}

		shuffleArray<T>(array: T[]): T[] {
				const newArray = [...array];
				for (let i = newArray.length - 1; i > 0; i--) {
						const j = Math.floor(Math.random() * (i + 1));
						[newArray[i], newArray[j]] = [newArray[j], newArray[i]];
				}
				return newArray;
		}

		GetResult(assessmentResponse: Map<string, number>): AssessmentResults {
				const testResults: AssessmentResults  = new AssessmentResults([], new Date(Date.now()));

				const zScores = new Map<string, number>();
				
				for(const spiritualGiftDefinedValue of this.constructData.keys()) {
						const spiritualGift = this.constructData.get(spiritualGiftDefinedValue);
						let totalResponse: number = 0;
						for (let construct of spiritualGift.Constructs ) {
								if ( assessmentResponse.has(construct)) {
										totalResponse += assessmentResponse.get(construct);
								}
						}
						
						const zScore = Math.round((totalResponse - spiritualGift.Mean) / spiritualGift.StandardDeviation);
						zScores.set(spiritualGiftDefinedValue, zScore);
						
						const percentage: number = this.zScoreToPercentage.get(zScore);
						
						// Get the category name from the categories array
						// since the constructData does not hold that information
						const spiritualGiftDefinedValueName = Object.values(this.categories).find((category: GiftCategory) => category.Guid === spiritualGiftDefinedValue);
						testResults.SpiritualGiftScores.push(new SpiritualGiftScore(spiritualGiftDefinedValue, spiritualGiftDefinedValueName.Value, zScore, percentage, totalResponse));
				}

				// Sort the scores from highest to lowest
				const orderedZScores = new Map([...zScores.entries()].sort((a, b) => b[1] - a[1]));

				// The DominantGifts should be populated based on the top 5 scores
				testResults.DominantGifts = Array.from(orderedZScores.keys()).slice(0, 5);

				// The SupportiveGifts should be populated based on a minimum score value, after bypassing the top 5
				testResults.SupportiveGifts = Array.from(orderedZScores.entries())
				.slice(5)
				.filter(([, value]) => value >= this.minSupportiveGiftScore)
				.map(([key]) => key);

				// The OtherGifts should be populated by all that remain
				testResults.OtherGifts = Array.from(orderedZScores.entries())
				.slice(5)
				.filter(([, value]) => value < this.minSupportiveGiftScore)
				.map(([key]) => key);

				return testResults;

		}

		SaveAssessmentResults(person: Person, assessmentResults: AssessmentResults): void {

				const delimitedDominantGifts = assessmentResults.DominantGifts.join(',');
				const delimitedSupportiveGifts = assessmentResults.SupportiveGifts.join(',');
				const delimitedOtherGifts = assessmentResults.OtherGifts.join(',');
				const lastSaveDate = assessmentResults.LastSaveDate.toString();

				const dominantGiftsAttribute: AttributeValue = {
					IsSystem: false,
					AttributeId: spiritualGiftsAttributes.core_DominantGifts.Id,
					EntityId: person.Id,
					Value: delimitedDominantGifts
				};
				const supportiveGiftsAttribute: AttributeValue = {
					IsSystem: false,
					AttributeId: spiritualGiftsAttributes.core_SupportiveGifts.Id,
					EntityId: person.Id,
					Value: delimitedSupportiveGifts
				};
				const otherGiftsAttribute: AttributeValue = {
					IsSystem: false,
					AttributeId: spiritualGiftsAttributes.core_OtherGifts.Id,
					EntityId: person.Id,
					Value: delimitedOtherGifts
				};
				const lastSaveDateAttribute: AttributeValue = {
					IsSystem: false,
					AttributeId: spiritualGiftsAttributes.core_SpiritualGiftsLastSaveDate.Id,
					EntityId: person.Id,
					Value: lastSaveDate
				};

				this.attributeValuesService.PostAttributeValues(dominantGiftsAttribute);
				this.attributeValuesService.PostAttributeValues(supportiveGiftsAttribute);
				this.attributeValuesService.PostAttributeValues(otherGiftsAttribute);
				this.attributeValuesService.PostAttributeValues(lastSaveDateAttribute);

		}

		LoadSavedAssessmentResults(person: Person, assessment?: Assessment) {

				const savedScores: AssessmentResults  = new AssessmentResults();

				// person.LoadAttributes();
				// savedScores.DominantGifts = person.GetAttributeValues( SpiritualGiftsService.ATTRIBUTE_DOMINANT_GIFTS);
				// savedScores.SupportiveGifts = person.GetAttributeValues( SpiritualGiftsService.ATTRIBUTE_SUPPORTIVE_GIFTS);
				// savedScores.OtherGifts = person.GetAttributeValues( SpiritualGiftsService.ATTRIBUTE_OTHER_GIFTS);
				// savedScores.LastSaveDate = person.GetAttributeValue( SpiritualGiftsService.ATTRIBUTE_LAST_SAVE_DATE);

				if (assessment) {
						// This might need some adjustment to understand what is actually being stored in the first place
						savedScores.SpiritualGiftScores = JSON.parse(assessment?.AssessmentResultData).ResultScores;

						return savedScores;
				}

				return savedScores;
		}

}
export interface GiftCategory {
		Id: number;
		IdKey: string;
		Value: string;
		Guid: string;
		MachineName: string;
		ForeignValue?: string; // This is the custom category name we define
		Questions?: {Id: string, Value: string}[];
		mean: number;
		standardDeviation: number;
		Constructs: string[];
}

export class SpiritualGift {

		Mean: number;
		StandardDeviation: number;
		Constructs: string[];

		/**
		 * Initializes a new instance of the SpiritualGift class.
		 * @param mean The mean.
		 * @param standardDeviation The standard deviation.
		 * @param constructs The constructs.
		 */
		constructor(mean: number, standardDeviation: number, constructs: string[]) {
				this.Mean = mean;
				this.StandardDeviation = standardDeviation;
				this.Constructs = constructs;
		}

}

export enum AssessmentRequestStatus {
		Pending = 'Pending', // 0
		Complete = 'Complete' // 1
}

export enum Gender {
		Unknown = 'Unknown', // 0
		Male = 'Male', // 1
		Female = 'Female' // 2
}

/**
 * This is a dummy class to fulfill the requirement to
 * extend the SpiritualGiftScore class. This is to match the RockRMS
 * class structure.
 */
export class RockDynamic {}

/**
 * This class actually extends the RockDynamic class.
 * It might not be essential to try to build the RockDynamic class
 * for the use of this service. See RockDynamic class.
 */
export class SpiritualGiftScore extends RockDynamic {
		
		DefinedValueGuid?: string; // The GUID
		SpiritualGiftName?: string;
		ZScore?: number;
		Percentage?: number;
		TotalScore?: number // This is a custom value of the total value

		constructor(spiritualGiftDefinedValue?: string, spiritualGiftDefinedValueName?: string, zScore?: number, percentage?: number, totalScore?: number) {
				super();
				this.DefinedValueGuid = spiritualGiftDefinedValue;
				this.SpiritualGiftName = spiritualGiftDefinedValueName;
				this.ZScore = zScore;
				this.Percentage = percentage;
				this.TotalScore = totalScore;
		}
}

export class AssessmentType implements RockModelAssessmentType {
		Id?: number = assessmentTypes.GIFTS.Id;
		Title: string;
		AssessmentPath: string;
		IsActive: boolean;
		RequiresRequest: boolean;
		IsSystem: boolean;
}

export class Person implements RockModelPerson {
		Gender: Gender;
		IsSystem: boolean;
		PrimaryAliasId?: number;
		FirstName?: string;
		LastName?: string;
		Email?: string;
		PhoneNumbers?: RockModelPhoneNumber[];
		ForeignKey?: string;
		ForeignId?: number;
		Id?: number;

		ConnectionGroup?: string; // This is a custom field not stored in Rock

		constructor() {
			this.ConnectionGroup = '',
			this.ForeignKey = 'Spiritual Gifts Test',
			this.FirstName = '',
			this.LastName = '',
			this.Email = '',
			this.Gender = null,
			this.IsSystem = false,
			this.PrimaryAliasId = null,
			this.Id = null,
			this.PhoneNumbers = [
				{
					Number: '',
					IsSystem: false,
					FullNumber: '',
					CountryCode: '1',
					IsMessagingEnabled: true,
					PersonId: null
				}
			]
		}
				
		// LoadAttributes() {}
		// SetAttributeValue(category: string, gifts: string) {}
		// SaveAttributeValues() {}
		// GetAttributeValues(category: string): string[] {return}
		// GetAttributeValue(value: string): Date {return}
}

export class PersonAlias implements RockModelPersonAlias {
		Name?: string;
		PersonId: number;
}

export class Assessment implements RockModelAssessment {
		public PersonAliasId: number;
		public AssessmentTypeId: number;
		public RequesterPersonAliasId?: number;
		public Status: AssessmentRequestStatus;
		public AssessmentResultData?: string;
		public CompletedDateTime?: string;
		public RequestedDateTime?: string;

		// Navigation Properties
		public RequesterPersonAlias?: PersonAlias;

		public toString(): string {
				return this.AssessmentResultData;
		}
}

export class AssessmentResults {
		SpiritualGiftScores?: SpiritualGiftScore[] = [];

		// These properties are string arrays of the GUID of the Spiritual Gift Type
		DominantGifts?: string[];
		SupportiveGifts?: string[];
		OtherGifts?: string[];
		LastSaveDate?: Date;

		constructor(spiritualGiftScores?: SpiritualGiftScore[], lastSaveDate?: Date) {
				this.SpiritualGiftScores = spiritualGiftScores;
				this.LastSaveDate = lastSaveDate;
		}
}

export class AssessmentResultData {
		Result: Map<string, number>;
		ResultScores: SpiritualGiftScore[];
		TimeToTake: number;
		constructor(result: Map<string, number>, scores: SpiritualGiftScore[], time: number) {
				this.Result = result;
				this.ResultScores = scores;
				this.TimeToTake = time;
		}
}

export class AssessmentResponse {
		Code: string;
		Question: string;
		Response?: number;
		ResponseString?: string = '';
}

export class AttributeValue implements RockModelAttributeValue {
	IsSystem: boolean = false;
	AttributeId: number;
	EntityId?: number;
	Value?: string;
}

export interface AllTestResults {
	testResults: AssessmentResults,
	resultData: Map<any, any>;
	gifts: GiftResult[],
	person: Person,
	assessmentResults: AssessmentResultData,
	categories: SpiritualGiftScore[];
	assessment: Assessment,
	assessmentResponses: AssessmentResponse[];
	ministryPositions?: MinistryPosition[];
	version: string;
}

export interface TestResults {
	[key:string]: GiftResult;
}

export interface GiftResult {
	key: string;
	gifts: SpiritualGiftScore[];
}

export interface MinistryArea {
	Section: string;
	Name: string;
	Description: string;
	IsActive: boolean;
	Groups: MinistryPosition[];
}
export interface MinistryPosition {
	Name: string;
	IsActive: boolean;
	Description: string;
	Constructs: number[];
	Gender: string[];
}